import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createCategory } from '../../utils/services/categoriesAPI';
import { getGroupPermission, addTeam } from '../../utils/services/teamsAPI';
import { createNewFolder } from '../../utils/services/folderAPI';
import styled from 'styled-components';
import { Form, Input, Button, Select, message } from 'antd';
import { NEW_TEMPLATE_SECTION } from './translationValues';

const { Option } = Select;

const StyledForm = styled(Form)`
  * {
    font-family: ui-sans-serif, system-ui, sans serif !important;
  }
  .ant-form-item {
    margin-bottom: 0;
  }
  .ant-btn-primary {
    background: linear-gradient(to right, #4f46e5, #7c3aed);
  }
`;

const CreateResource = props => {
  const { t } = useTranslation(['NEW_TEMPLATE_SECTION']);
  const { form, selectedView, organizationView } = props;
  const { getFieldDecorator, validateFields, resetFields } = form;

  const [creating, setCreating] = useState(false);
  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);

  /* fetch teams only for folders */
  useEffect(() => {
    if (organizationView === 'folders') {
      fetchTeams();
    }
  }, [organizationView]);

  const fetchTeams = async () => {
    setLoadingTeams(true);
    try {
      const response = await getGroupPermission();
      if (response?.statusCode === 1410 && response?.data) {
        setTeams(response.data);
      } else {
        message.error(
          t(
            NEW_TEMPLATE_SECTION.LOAD_TEAMS_FAILED.key,
            NEW_TEMPLATE_SECTION.LOAD_TEAMS_FAILED.value,
          ),
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingTeams(false);
    }
  };

  const handleCreate = e => {
    e.preventDefault();

    validateFields(async (err, values) => {
      if (err) return;

      setCreating(true);

      try {
        if (organizationView === 'categories') {
          const response = await createCategory({
            name: values.categoryName,
            type: selectedView === 'documents' ? 'template' : 'element',
          });
          if (response?.statusCode === 501) {
            message.success(
              t(NEW_TEMPLATE_SECTION.CATEGORY_CREATED.key, NEW_TEMPLATE_SECTION.CATEGORY_CREATED.value),
            );
          } else {
            message.error(
              t(
                NEW_TEMPLATE_SECTION.CATEGORY_CREATION_FAILED.key,
                NEW_TEMPLATE_SECTION.CATEGORY_CREATION_FAILED.value,
              ),
            );
          }
        }

        if (organizationView === 'folders') {
          const response = await createNewFolder({
            parentId: null,
            name: values.folderName,
            resourceGroup: values.teamId,
          });
          if (response?.statusCode === 601) {
            message.success(
              t(NEW_TEMPLATE_SECTION.FOLDER_CREATED.key, NEW_TEMPLATE_SECTION.FOLDER_CREATED.value),
            );
          } else {
            message.error(
              t(
                NEW_TEMPLATE_SECTION.FOLDER_CREATION_FAILED.key,
                NEW_TEMPLATE_SECTION.FOLDER_CREATION_FAILED.value,
              ),
            );
          }
        }

        if (organizationView === 'teams') {
          const response = await addTeam({ name: values.teamName });
          if (response?.statusCode === 1402) {
            message.success(
              t(NEW_TEMPLATE_SECTION.TEAM_CREATED.key, NEW_TEMPLATE_SECTION.TEAM_CREATED.value),
            );
          } else {
            message.error(
              t(
                NEW_TEMPLATE_SECTION.TEAM_CREATION_FAILED.key,
                NEW_TEMPLATE_SECTION.TEAM_CREATION_FAILED.value,
              ),
            );
          }
        }

        resetFields();
      } catch (error) {
        console.error(error);
      } finally {
        setCreating(false);
      }
    });
  };

  const renderCategory = () => (
    <>
      <h5>
        {t(NEW_TEMPLATE_SECTION.CREATE_CATEGORY.key, NEW_TEMPLATE_SECTION.CREATE_CATEGORY.value)}
      </h5>
      <Form.Item>
        {getFieldDecorator('categoryName', {
          rules: [
            {
              required: true,
              message: t(
                NEW_TEMPLATE_SECTION.CATEGORY_NAME_REQUIRED.key,
                NEW_TEMPLATE_SECTION.CATEGORY_NAME_REQUIRED.value,
              ),
            },
          ],
        })(
          <Input
            placeholder={t(
              NEW_TEMPLATE_SECTION.ENTER_CATEGORY_NAME.key,
              NEW_TEMPLATE_SECTION.ENTER_CATEGORY_NAME.value,
            )}
          />,
        )}
      </Form.Item>
    </>
  );

  const renderTeam = () => (
    <>
      <h5>
        {t(NEW_TEMPLATE_SECTION.CREATE_TEAM.key, NEW_TEMPLATE_SECTION.CREATE_TEAM.value)}
      </h5>
      <Form.Item>
        {getFieldDecorator('teamName', {
          rules: [
            {
              required: true,
              message: t(
                NEW_TEMPLATE_SECTION.TEAM_NAME_REQUIRED.key,
                NEW_TEMPLATE_SECTION.TEAM_NAME_REQUIRED.value,
              ),
            },
          ],
        })(
          <Input
            placeholder={t(
              NEW_TEMPLATE_SECTION.ENTER_TEAM_NAME.key,
              NEW_TEMPLATE_SECTION.ENTER_TEAM_NAME.value,
            )}
          />,
        )}
      </Form.Item>
    </>
  );

  const renderFolder = () => (
    <>
      <h5>
        {t(NEW_TEMPLATE_SECTION.CREATE_FOLDER.key, NEW_TEMPLATE_SECTION.CREATE_FOLDER.value)}
      </h5>

      <Form.Item>
        {getFieldDecorator('folderName', {
          rules: [
            {
              required: true,
              message: t(
                NEW_TEMPLATE_SECTION.FOLDER_NAME_REQUIRED.key,
                NEW_TEMPLATE_SECTION.FOLDER_NAME_REQUIRED.value,
              ),
            },
          ],
        })(
          <Input
            placeholder={t(
              NEW_TEMPLATE_SECTION.ENTER_FOLDER_NAME.key,
              NEW_TEMPLATE_SECTION.ENTER_FOLDER_NAME.value,
            )}
          />,
        )}
      </Form.Item>

      <Form.Item>
        {getFieldDecorator('teamId', {
          rules: [
            {
              required: true,
              message: t(
                NEW_TEMPLATE_SECTION.TEAM_REQUIRED.key,
                NEW_TEMPLATE_SECTION.TEAM_REQUIRED.value,
              ),
            },
          ],
        })(
          <Select
            placeholder={t(
              NEW_TEMPLATE_SECTION.SELECT_TEAM.key,
              NEW_TEMPLATE_SECTION.SELECT_TEAM.value,
            )}
            loading={loadingTeams}
          >
            {teams.map(team => (
              <Option key={team.rgId} value={team.rgId}>
                {team.groupName}
              </Option>
            ))}
          </Select>,
        )}
      </Form.Item>
    </>
  );

  const renderByView = () => {
    if (organizationView === 'categories') return renderCategory();
    if (organizationView === 'teams') return renderTeam();
    if (organizationView === 'folders') return renderFolder();
    return null;
  };

  return (
    <StyledForm onSubmit={handleCreate}>
      {renderByView()}

      <Form.Item>
        <Button type="primary" htmlType="submit" loading={creating}>
          {t(NEW_TEMPLATE_SECTION.CREATE.key, NEW_TEMPLATE_SECTION.CREATE.value)}
        </Button>
      </Form.Item>
    </StyledForm>
  );
};

export default Form.create()(CreateResource);
